/**
 * @file schemas.ts
 * @brief Zod schemas + TS types for the Super Admin → Users feature.
 *
 * @details
 * Mirrors the response shapes produced by `BACKEND/src/features/admin/`:
 * - `service.js::getAllUsers` returns `{ users, pagination }`. Each user
 *   row has the SQL-cased keys (`first_name`, `last_name`, `company_id`,
 *   `created_at`, `last_login_at`, `session_count`, `total_cost_cents`,
 *   `company_name`).
 * - `service.js::createUser` accepts `{ email, password, first_name, … }`
 *   and returns the created row.
 *
 * We intentionally use `passthrough()` on the user object so the panel
 * survives backend additions (e.g. profile_image_url variants) without
 * Zod parse failures.
 *
 * @references
 * - BACKEND/src/features/admin/service.js (getAllUsers, createUser, updateUser)
 * - BACKEND/src/features/admin/routes.js (route shapes)
 *
 * @author DES
 * @date 26-05-2026
 */

import { z } from "zod";
import { UserRoleSchema, UserStatusSchema } from "@/shared/types/api";

// ----------------------------------------------------------------------
// Admin user row (list endpoint shape)
// `session_count` and `total_cost_cents` come from the SQL aggregations
// in getAllUsers — they're returned as strings sometimes (Postgres COUNT
// → bigint → string in node-postgres), so we coerce to number.
// ----------------------------------------------------------------------
const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

// ----------------------------------------------------------------------
// Login event (login-location audit).
// Mirrors BACKEND login_events rows returned by getUserById.last_login_location
// and getUserLoginHistory. All geo fields are nullable (private/loopback IPs
// resolve to no location).
// ----------------------------------------------------------------------
export const LoginEventSchema = z
  .object({
    id: z.string().optional(),
    ip_address: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    region: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    timezone: z.string().nullable().optional(),
    user_agent: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
  })
  .passthrough();

export type LoginEvent = z.infer<typeof LoginEventSchema>;

export const LoginHistoryResponseSchema = z.object({
  history: z.array(LoginEventSchema),
});

export const AdminUserSchema = z
  .object({
    id: z.string(),
    email: z.string().email(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    phone_number: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    role: UserRoleSchema.catch("user"),
    status: UserStatusSchema.catch("active"),
    company: z.string().nullable().optional(),
    company_id: z.string().nullable().optional(),
    company_name: z.string().nullable().optional(),
    profile_image_url: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    last_login_at: z.string().nullable().optional(),
    // Most recent login location (from login_events). Present on the list
    // response; the detail response also carries `last_login_location`.
    last_login_country: z.string().nullable().optional(),
    last_login_region: z.string().nullable().optional(),
    last_login_city: z.string().nullable().optional(),
    last_login_location: LoginEventSchema.nullable().optional(),
    session_count: numericString.optional().default(0),
    total_cost_cents: numericString.optional().default(0),
    total_sessions: numericString.optional(),
    // Meeting Intel voiceprint — typically `none` | `enrolled` (11-07-2026).
    // Additive; older backends omit the field → treat as not enrolled.
    voice_enrollment_status: z.string().nullable().optional().catch("none"),
    // Per-user native language ISO code (13-07-2026). Additive; null/omit
    // when unset — Users list shows flag + label via LanguageFlag.
    native_language: z.string().nullable().optional(),
    /**
     * The post-signup questions' answers (23-09-2026).
     *
     * ⚠️ Present on `GET /admin/users/:id` ONLY, which selects `u.*`. The LIST
     * endpoint names its columns explicitly and does not include these, so
     * anything reading them has to go through `useUser(id)` — a list row will
     * always have them undefined.
     *
     * ⚠️ A null answer beside a null `*_at` means "never asked, or skipped",
     * which is a different fact from "asked and declined". Codes, never
     * labels: `FRONTEND/src/features/auth/onboardingApi.ts` owns the lists and
     * a copy change there must not fork the data.
     */
    signup_source: z.string().nullable().optional(),
    signup_source_detail: z.string().nullable().optional(),
    signup_source_at: z.string().nullable().optional(),
    primary_use: z.string().nullable().optional(),
    primary_use_at: z.string().nullable().optional(),
    // ADR-019 seat (25-07-2026). Null / omit → pay as you go (Basic).
    subscription_plan_code: z.string().nullable().optional(),
    /** Our own account (audit #19, 18-10-2026): kept out of company and
     *  superadmin totals. Absent on older backends → toggle hidden. */
    is_internal: z.boolean().nullable().optional(),
    /** The Owner account (B31, ADR-059): no role / status / delete / reset
     *  through any screen. Absent on older backends. */
    is_owner: z.boolean().nullable().optional(),
    /** Two-step verification on (B31). Absent on older backends → no reset. */
    two_factor_enabled: z.boolean().nullable().optional(),
    subscription_status: z.string().nullable().optional(),
    subscription_cancel_at_period_end: z
      .union([z.boolean(), z.null()])
      .optional()
      .catch(false),
    subscription_period_end: z.string().nullable().optional(),
    subscription_period_start: z.string().nullable().optional(),
    subscription_used_minutes: numericString.nullable().optional(),
    subscription_included_minutes: numericString.nullable().optional(),
    subscription_remaining_minutes: numericString.nullable().optional(),
    // ADR-020 company Enterprise (27-07-2026). Company can be on Enterprise
    // while this user is still PAYG until a seat is assigned.
    enterprise_active: z.boolean().optional().catch(false),
    enterprise_seated: z.boolean().optional().catch(false),
    enterprise_included_minutes: numericString.nullable().optional(),
    enterprise_used_minutes: numericString.nullable().optional(),
    enterprise_period_end: z.string().nullable().optional(),
  })
  .passthrough();

export type AdminUser = z.infer<typeof AdminUserSchema>;

// ----------------------------------------------------------------------
// Pagination envelope (mirrors BACKEND admin/service.js::getAllUsers)
// ----------------------------------------------------------------------
export const AdminPaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

export const AdminUsersListResponseSchema = z.object({
  users: z.array(AdminUserSchema),
  pagination: AdminPaginationSchema,
});

export type AdminUsersListResponse = z.infer<
  typeof AdminUsersListResponseSchema
>;

// ----------------------------------------------------------------------
// List query params (sent to GET /api/admin/users)
// ----------------------------------------------------------------------
export interface AdminUsersListParams {
  page?: number;
  limit?: number;
  role?: "user" | "admin" | "superadmin";
  status?: "active" | "suspended" | "deleted";
  company_id?: string;
  search?: string;
  /** ISO base code filter (e.g. `ur` / `en`) — matches `users.native_language`. */
  native_language?: string;
  /**
   * ADR-019 billing filter: `basic` (payg), `subscribed`, or a plan code
   * (`starter` | `pro` | `business`).
   */
  billing?: "basic" | "subscribed" | "starter" | "pro" | "business";
}

// ----------------------------------------------------------------------
// Create input
// Backend createUser expects snake_case fields; we mirror that here.
// Password validation matches BACKEND admin/routes.js password policy
// (min 8, mixed case, digit, symbol, no whitespace).
// ----------------------------------------------------------------------
const passwordSchema = z
  .string()
  .min(8, "At least 8 characters")
  .regex(/[A-Z]/, "Add an uppercase letter")
  .regex(/[a-z]/, "Add a lowercase letter")
  .regex(/[0-9]/, "Add a number")
  .regex(/[!@#$%^&*()_+\-=[\]{};:'",.<>/?\\|]/, "Add a symbol")
  .refine((v) => !/\s/.test(v), "No whitespace");

export const CreateUserInputSchema = z.object({
  email: z.string().email("Invalid email"),
  password: passwordSchema,
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  phone_number: z.string().optional().or(z.literal("")),
  title: z.string().optional().or(z.literal("")),
  role: UserRoleSchema.default("user"),
  status: UserStatusSchema.default("active"),
  company_id: z.string().optional().or(z.literal("")),
});

export type CreateUserInput = z.infer<typeof CreateUserInputSchema>;

// ----------------------------------------------------------------------
// Update input — every field optional; sent to PUT /api/admin/users/:id
// ----------------------------------------------------------------------
export const UpdateUserInputSchema = z.object({
  email: z.string().email("Invalid email").optional(),
  first_name: z.string().min(1).optional(),
  last_name: z.string().min(1).optional(),
  phone_number: z.string().optional().or(z.literal("")),
  title: z.string().optional().or(z.literal("")),
  role: UserRoleSchema.optional(),
  status: UserStatusSchema.optional(),
  company_id: z.string().optional().nullable(),
  is_internal: z.boolean().optional(),
});

export type UpdateUserInput = z.infer<typeof UpdateUserInputSchema>;

// ----------------------------------------------------------------------
// Change password — strength rules identical to backend
// (BACKEND/src/features/admin/routes.js::validatePasswordStrength).
// ----------------------------------------------------------------------
export const ChangeUserPasswordInputSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

export type ChangeUserPasswordInput = z.infer<
  typeof ChangeUserPasswordInputSchema
>;
